import { Cache, UpdateResolver } from '@urql/exchange-graphcache';
import isThisWeek from 'date-fns/isThisWeek';
import isToday from 'date-fns/isToday';
import _find from 'lodash/find';
import _isMatch from 'lodash/isMatch';
import isNil from 'lodash/isNil';
import _keyBy from 'lodash/keyBy';

import { inspectCache, invalidateQuery, updateCache } from '@/helper/urqlClient/cache/cacheHelpers';
import { UpdateEntityTagsMutation } from '@/modules/corporate/modules/tags/graphql/mutations/generated/updateEntityTags';
import { REVERSE_MAPPED_PRIORITY } from '@/modules/tasks/views/TasksDashboard/components/GroupedList/hooks/useGroupedTable';
import { UpdateEntityAttachmentMutation } from '@/modules/tasks/views/TasksDashboard/graphql/mutations/generated/updateEntityAttachment';
import {
  FileDocument,
  FileQuery,
  FileQueryVariables,
} from '@/modules/tasks/views/TasksDashboard/graphql/queries/generated/file';
import {
  TaskDetailsDocument,
  TaskDetailsQuery,
  TaskDetailsQueryVariables,
} from '@/modules/tasks/views/TasksDashboard/graphql/queries/generated/taskDetails';
import {
  ActionItemPriorityEnum,
  ActionItemSubscriptionOperationEnum,
  ActionItemSubscriptionPayload,
  RootActionItemFilter,
  ToggleTimerPayload,
} from '@/types/schema';

import {
  PaginatedGroupedActionItemsDocument,
  PaginatedGroupedActionItemsQuery,
  PaginatedGroupedActionItemsQueryVariables,
} from '../../../../../graphql/queries/generated/paginatedGroupedActionItems';
import { isGroupedByTag } from '../../../../../views/TasksDashboard/components/TaskGroupByFilter/utils';
import getPaginationFilters from '../../../../../views/TasksDashboard/hooks/useGroupedActionItems/getPaginationFilters';
import { ActionItemFragment } from '../../fragments/generated/ActionItem';
import { CreateActionItemAndUpdateMetricsMutation } from '../../mutations/generated/createActionItemAndUpdateMetrics';
import { DeleteActionItemAndUpdateMetricsMutation } from '../../mutations/generated/deleteActionItemAndUpdateMetrics';
import { UpdateActionItemMutation } from '../../mutations/generated/updateActionItem';
import { UpdateActionItemAndUpdateMetricsMutation } from '../../mutations/generated/updateActionItemAndUpdateMetrics';
import {
  ActionItemsDocument,
  ActionItemsQuery,
  ActionItemsQueryVariables,
} from '../../queries/generated/actionItems';

const newBlankGroup = () => ({
  groupByKey: '',
  groupedEntity: {
    totalCount: 0,
    edges: [],
    __typename: 'PaginatedActionItemsPayload' as 'PaginatedActionItemsPayload',
  },
  __typename: 'GroupedActionItemEdge' as 'GroupedActionItemEdge',
});

const getGroupOrder: Record<string, Record<string, number>> = {
  status: { todo: 0, inProgress: 1, inReview: 2, completed: 3 },
  priority: { urgent: 0, high: 1, medium: 2, low: 3, noPriority: 4 },
  dueDate: { Overdue: 0, Today: 1, 'This week': 2, Future: 3, Done: 4, 'No Due Date': 5 },
};

const findIfExist = <T>(array?: T[] | null, item?: T) => {
  if (array?.length && item) {
    return array.includes(item);
  }
};

const getSelectedGroup = (actionItem: ActionItemFragment, groupName: keyof ActionItemFragment) => {
  const value = actionItem[groupName] as string;
  if (groupName === 'priority') {
    const groupKey = REVERSE_MAPPED_PRIORITY[value as ActionItemPriorityEnum];
    if (!groupKey) {
      throw new Error(`Invalid priority value: ${value}`);
    }
    return groupKey;
  }

  if (groupName === 'dueDate') {
    if (actionItem['status'] === 'completed') return 'Done';
    if (!value) {
      return 'No Due Date';
    }
    const valueDate = new Date(value);
    if (new Date() > valueDate) {
      return 'Overdue';
    }
    if (isToday(valueDate)) {
      return 'Today';
    }
    if (isThisWeek(valueDate)) {
      return 'This week';
    }

    return 'Future';
  }

  if (isGroupedByTag(groupName)) {
    const selectedTag = _find(actionItem.tags, { tagId: groupName.split(':')[1] });

    return selectedTag?.optionIdValues?.[0] || selectedTag?.users?.[0]?.id;
  }

  return value;
};

// function handleFileWidgetMetricsUpdate(cache: Cache, actionItem: any) {
//   const fileId = actionItem?.file?.id;
//   const widgetMetrics = actionItem?.file?.widgetMetrics;

//   if (!fileId || !widgetMetrics) {
//     return;
//   }

//   inspectCache(cache, 'Query', 'file', (field) => {
//     const args = field.arguments as FileQueryVariables;
//     if (args.filter?.id === fileId) {
//       updateCache<FileQuery, FileQueryVariables>(cache, FileDocument, args, (data) => {
//         if (data?.file) {
//           data.file.widgetMetrics = widgetMetrics;
//         }
//         return data;
//       });
//     }
//   });
// }

function handleOrderChange(cache: Cache, actionItem?: ActionItemFragment | null) {
  if (!actionItem) {
    return;
  }
  const allActionItemsEdges: any[] = [];
  inspectCache(cache, 'Query', 'actionItems', (field) => {
    const args = field.arguments as ActionItemsQueryVariables;
    if (!args.first) {
      return;
    }

    const actionItemQueryData = cache.readQuery({
      query: ActionItemsDocument,
      variables: args,
    }) as ActionItemsQuery;
    if (actionItemQueryData && actionItemQueryData?.actionItems?.edges?.length) {
      const edges = actionItemQueryData?.actionItems?.edges;
      allActionItemsEdges.push(...edges);
    }
  });

  allActionItemsEdges.forEach((actionItemEdge) => {
    if (actionItemEdge.node.id == actionItem.id) {
      actionItemEdge.node['order'] = actionItem.order;
    }
  });

  allActionItemsEdges.sort((a, b) => a?.node?.order - b?.node?.order);

  let currentIndex = 0;
  const totalCount = allActionItemsEdges.length ?? 0;
  inspectCache(cache, 'Query', 'actionItems', (field) => {
    const args = field.arguments as ActionItemsQueryVariables;
    if (!args.first) {
      return;
    }

    updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
      cache,
      ActionItemsDocument,
      args,
      (actionItemQueryData) => {
        if (actionItemQueryData?.actionItems?.edges?.length) {
          const size = actionItemQueryData?.actionItems?.edges?.length ?? 25;
          const upparBoundIndex = Math.min(currentIndex + size, totalCount);
          const edges = allActionItemsEdges.slice(currentIndex, upparBoundIndex);
          actionItemQueryData.actionItems.edges = edges;
          currentIndex = upparBoundIndex;
        }
        return actionItemQueryData;
      },
    );
  });
}

function handleActionItemsOnTaskDetailPageChange(
  cache: Cache,
  actionItem?: ActionItemFragment | null,
) {
  if (!actionItem) {
    return;
  }
  const allActionItemsEdges: any[] = [];
  let isUpdated = false;
  inspectCache(cache, 'Query', 'actionItems', (field) => {
    const args = field.arguments as ActionItemsQueryVariables;
    if (!args.first || isUpdated) {
      return;
    }
    const actionItemQueryData = cache.readQuery({
      query: ActionItemsDocument,
      variables: args,
    }) as ActionItemsQuery;
    const data = actionItemQueryData?.actionItems?.edges?.find(
      (edge) => edge.node.id == actionItem.id,
    );
    const index =
      actionItemQueryData?.actionItems?.edges?.findIndex(
        (edge) => edge.node.id === actionItem.id,
      ) ?? -1;
    if (index != -1) {
      isUpdated = true;
      updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
        cache,
        ActionItemsDocument,
        args,
        (actionItemQueryData) => {
          if (actionItemQueryData?.actionItems?.edges?.[index]) {
            for (const [key, value] of Object.entries(
              actionItemQueryData.actionItems.edges[index].node,
            )) {
              // update any stale field if the same field is present in the new actionItem
              if (actionItem.hasOwnProperty(key)) {
                // @ts-ignore
                actionItemQueryData.actionItems.edges[index].node[key] = actionItem[key];
              }
            }
          }
          return actionItemQueryData;
        },
      );
    }
  });
}
function handleTaskDetailUpdageChange(
  cache: Cache,
  data: any,
  actionItem?: ActionItemFragment | null,
) {
  if (!actionItem) {
    return;
  }
  inspectCache(cache, 'Query', 'actionItem', (field) => {
    const args = field.arguments as TaskDetailsQueryVariables;
    const actionItemQueryData = cache.readQuery({
      query: TaskDetailsDocument,
      variables: args,
    }) as TaskDetailsQuery;
    if (actionItemQueryData?.actionItem.actionItem?.id == actionItem.id) {
      updateCache<TaskDetailsQuery, TaskDetailsQueryVariables>(
        cache,
        TaskDetailsDocument,
        args,
        (actionItemQueryData) => {
          if (actionItemQueryData?.actionItem?.actionItem) {
            for (const [key, value] of Object.entries(actionItemQueryData.actionItem.actionItem)) {
              // update any stale field if the same field is present in the new actionItem
              if (actionItem.hasOwnProperty(key)) {
                // @ts-ignore
                actionItemQueryData.actionItem.actionItem[key] = actionItem[key];
              }
            }
          }
          return actionItemQueryData;
        },
      );
    }
  });
}

function handleActionItemUpdate(cache: Cache, actionItem?: ActionItemFragment | null) {
  if (!actionItem) {
    return;
  }

  inspectCache(cache, 'Query', 'paginatedGroupedActionItems', (field) => {
    const args = field.arguments as PaginatedGroupedActionItemsQueryVariables;
    // only update cache of paginatedGroupedActionItems if
    // 1. the updated action item is not a subtask
    // OR
    // 2. the view is set to "Separate", i.e. both tasks and sub-tasks are visible at the top level
    const shouldUpdateCache =
      !actionItem.parentId || args.filters?.rootActionItemStatus === RootActionItemFilter.Both;
    if (shouldUpdateCache && args.groupBy) {
      const groupByKey = args.groupBy as keyof typeof actionItem;
      updateCache<PaginatedGroupedActionItemsQuery, PaginatedGroupedActionItemsQueryVariables>(
        cache,
        PaginatedGroupedActionItemsDocument,
        args,
        (data) => {
          /* check if actionItem is in the latest group if so we don't need to update it
            else we need add actionItem in the latest group and find and remove from old group remove
          */
          if (data?.paginatedGroupedActionItems?.edges) {
            const selectedGroup = getSelectedGroup(actionItem, groupByKey);
            if (typeof selectedGroup === 'undefined') {
              if (isGroupedByTag(groupByKey)) {
                data.paginatedGroupedActionItems.edges =
                  data?.paginatedGroupedActionItems?.edges.filter(({ node }) => {
                    if (node.groupedEntity?.edges?.length) {
                      node.groupedEntity.edges = node.groupedEntity.edges.filter(
                        ({ node }) =>
                          node?.tags?.find((tag) => groupByKey.indexOf(tag?.tagId!) !== -1),
                      );
                    }

                    return node.groupedEntity.edges?.length;
                  });
              }

              return data;
            }

            const groupOrder = getGroupOrder[groupByKey] || {};

            let { [selectedGroup]: expectedGroup, ...restGroups } = _keyBy(
              data?.paginatedGroupedActionItems?.edges.map(({ node }) => {
                node.groupedEntity.edges?.map((edge) => {
                  if (edge.node.id == actionItem.id) {
                    edge.node = actionItem;
                  }
                });
                return node;
              }),
              'groupByKey',
            );
            const taskIndex =
              expectedGroup?.groupedEntity.edges?.findIndex(
                (task) => task.node.id === actionItem.id,
              ) ?? -1;
            // if actionItem is not in correct group then add in the group and remove from other group
            if (taskIndex === -1) {
              // remove item from old group
              Object.values(restGroups).forEach((edge) => {
                if (edge?.groupedEntity.edges?.length) {
                  const previousSize = edge.groupedEntity.edges.length;
                  edge.groupedEntity.edges = edge.groupedEntity.edges.filter(
                    (item) => item.node.id !== actionItem.id,
                  );

                  // to update actionItems query which are fetch for each group in pagination
                  inspectCache(cache, 'Query', 'actionItems', (field) => {
                    const args = field.arguments as ActionItemsQueryVariables;
                    // not a pagination query
                    if (!args.first || !args.filters) {
                      return;
                    }
                    const paginationFilter = getPaginationFilters(
                      edge.groupByKey,
                      groupByKey,
                      actionItem.tags,
                    );

                    if (_isMatch(args.filters, paginationFilter)) {
                      updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
                        cache,
                        ActionItemsDocument,
                        args,
                        (actionItemQueryData) => {
                          if (actionItemQueryData?.actionItems?.edges?.length) {
                            actionItemQueryData.actionItems.edges =
                              actionItemQueryData.actionItems.edges.filter(
                                (item) => item.node.id !== actionItem.id,
                              );
                          }

                          return actionItemQueryData;
                        },
                      );
                    }
                  });

                  const itemsRemoved = previousSize - edge.groupedEntity.edges.length;
                  if (!isNil(edge.groupedEntity.totalCount) && itemsRemoved > 0) {
                    edge.groupedEntity.totalCount -= itemsRemoved;
                  }
                }
              });

              if (!expectedGroup) {
                expectedGroup = { ...newBlankGroup(), groupByKey: selectedGroup };
                data?.paginatedGroupedActionItems?.edges.push({
                  node: expectedGroup,
                  __typename: 'PaginatedGroupedActionItemEdge',
                });

                // sort the groups by order
                data?.paginatedGroupedActionItems?.edges.sort(
                  (edge1, edge2) =>
                    groupOrder[edge1.node.groupByKey] - groupOrder[edge2.node.groupByKey],
                );
              }
              // add item to expected group
              // todo need to add sort logic while adding new item
              expectedGroup.groupedEntity.edges?.unshift({
                node: actionItem,
                __typename: 'ActionItemEdge',
              });
              if (!isNil(expectedGroup.groupedEntity.totalCount)) {
                expectedGroup.groupedEntity.totalCount++;
              }
            }

            // remove actionItems with doesn't match filters
            data?.paginatedGroupedActionItems?.edges.map(({ node }) => {
              if (!node.groupedEntity.edges) {
                return node;
              }
              const previousSize = node.groupedEntity.edges.length;
              node.groupedEntity.edges = node.groupedEntity.edges.filter((item) => {
                const priorityMatch =
                  findIfExist(
                    args.filters?.priorities,
                    item.node.priority?.toUpperCase() as ActionItemPriorityEnum,
                  ) ?? true;
                const assignedToIdMatch =
                  findIfExist(args.filters?.assignedToIds, item.node.assignedToId) ?? true;

                const fileIdsMatch = findIfExist(args.filters?.fileIds, item.node.fileId) ?? true;

                const createdByMatch =
                  findIfExist(args.filters?.createdByIds, item.node.createdById) ?? true;

                let unCategoryMatch = true;
                // keep items without fileId for unCategory
                if (args.filters?.unCategorized === false) {
                  unCategoryMatch = !item.node.fileId;
                }

                return (
                  priorityMatch &&
                  assignedToIdMatch &&
                  fileIdsMatch &&
                  unCategoryMatch &&
                  createdByMatch
                );
              });

              const itemsRemoved = previousSize - node.groupedEntity.edges.length;
              if (!isNil(node.groupedEntity.totalCount) && itemsRemoved > 0) {
                node.groupedEntity.totalCount -= itemsRemoved;
              }

              return node;
            });
          }

          // remove empty groups
          if (data?.paginatedGroupedActionItems?.edges.length) {
            data.paginatedGroupedActionItems.edges =
              data?.paginatedGroupedActionItems?.edges.filter(
                (edges) => edges.node.groupedEntity.edges?.length,
              );
          }

          return data;
        },
      );
    }
  });

  if (actionItem.parentId) {
    // to update actionItems which are fetch when expanding subtasks
    inspectCache(cache, 'Query', 'actionItems', (field) => {
      const args = field.arguments as ActionItemsQueryVariables;
      // not a subtask query
      if (!args.filters?.parentIds) {
        return;
      }

      updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
        cache,
        ActionItemsDocument,
        args,
        (actionItemQueryData) => {
          if (actionItemQueryData?.actionItems?.edges?.length) {
            const staleActionItem = actionItemQueryData.actionItems.edges.find(
              (item) => item.node.id === actionItem.id,
            )?.node;
            if (staleActionItem) {
              for (const [key, value] of Object.entries(staleActionItem)) {
                // update any stale field if the same field is present in the new actionItem
                if (actionItem.hasOwnProperty(key)) {
                  // @ts-ignore
                  staleActionItem[key] = actionItem[key];
                }
              }
            }
          }

          return actionItemQueryData;
        },
      );
    });
  }
}

export function getActionItemUpdater(): UpdateResolver {
  return (result, args, cache) => {
    const actionItemsPayload = result.actionItems as ActionItemSubscriptionPayload;
    const operationType = actionItemsPayload.operation;
    const actionItem = actionItemsPayload.actionItem;

    switch (operationType) {
      case ActionItemSubscriptionOperationEnum.ActionItemUpdated: {
        handleActionItemUpdate(cache, actionItem as ActionItemFragment);
        break;
      }
      default:
        return;
    }
  };
}

export function updateActionItemMutation(): UpdateResolver {
  return (result, args, cache) => {
    const allQueries = cache.inspectFields('Query');
    const { data } = (args ?? {}) as any;
    const isOrderChanged = data?.order;
    const actionItem = (result as UpdateActionItemMutation)?.updateActionItem?.actionItem;
    // if (actionItem?.file) {
    //   handleFileWidgetMetricsUpdate(cache, actionItem);
    // }
    handleTaskDetailUpdageChange(cache, data, actionItem as ActionItemFragment);
    if (isOrderChanged && result?.updateActionItem) {
      handleOrderChange(cache, actionItem as ActionItemFragment);
    } else {
      handleActionItemsOnTaskDetailPageChange(cache, actionItem as ActionItemFragment);
    }
    handleActionItemUpdate(cache, actionItem);
  };
}

export function toggleTimerMutation(): UpdateResolver {
  return (result, args, cache, info) => {
    invalidateQuery('timeLogs')(result, args, cache, info);
    const updatedEntities = (result?.toggleTimer as ToggleTimerPayload)?.updatedEntitiesTimeSpent;

    if (!updatedEntities?.length) {
      return;
    }

    const updatesMap = new Map<string, number>();
    updatedEntities.forEach((item) => {
      if (!isNil(item.totalTimeSpent)) {
        updatesMap.set(item.entityId, item.totalTimeSpent);
      }
    });

    //1. updating cache for "paginatedGroupedActionItems"

    inspectCache(cache, 'Query', 'paginatedGroupedActionItems', (field) => {
      const args = field.arguments as PaginatedGroupedActionItemsQueryVariables;
      if (!args.first) {
        return;
      }
      updateCache<PaginatedGroupedActionItemsQuery, PaginatedGroupedActionItemsQueryVariables>(
        cache,
        PaginatedGroupedActionItemsDocument,
        args,
        (queryData) => {
          if (queryData?.paginatedGroupedActionItems?.edges) {
            queryData.paginatedGroupedActionItems.edges.forEach((groupEdge) => {
              if (groupEdge.node.groupedEntity.edges) {
                groupEdge.node.groupedEntity.edges.forEach((itemEdge) => {
                  const newTotalTimeSpent = updatesMap.get(itemEdge.node.id);
                  if (typeof newTotalTimeSpent !== 'undefined') {
                    itemEdge.node.totalTimeSpent = newTotalTimeSpent;
                  }
                });
              }
            });
          }
          return queryData;
        },
      );
    });

    //2. updating cache for "actionItems" (subtasks)

    inspectCache(cache, 'Query', 'actionItems', (field) => {
      const args = field.arguments as ActionItemsQueryVariables;
      updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
        cache,
        ActionItemsDocument,
        args,
        (queryData) => {
          if (queryData?.actionItems?.edges) {
            queryData.actionItems.edges.forEach((itemEdge) => {
              const newTotalTimeSpent = updatesMap.get(itemEdge.node.id);
              if (typeof newTotalTimeSpent !== 'undefined') {
                itemEdge.node.totalTimeSpent = newTotalTimeSpent;
              }
            });
          }
          return queryData;
        },
      );
    });

    //3. updating cache for "actionItem"
    inspectCache(cache, 'Query', 'actionItem', (field) => {
      const args = field.arguments as TaskDetailsQueryVariables;
      updateCache<TaskDetailsQuery, TaskDetailsQueryVariables>(
        cache,
        TaskDetailsDocument,
        args,
        (queryData) => {
          if (queryData?.actionItem?.actionItem) {
            const newTotalTimeSpent = updatesMap.get(queryData.actionItem.actionItem.id);
            if (typeof newTotalTimeSpent !== 'undefined') {
              queryData.actionItem.actionItem.totalTimeSpent = newTotalTimeSpent;
            }
          }
          return queryData;
        },
      );
    });
  };
}

const handleFileWidgetMetricsUpdate = (cache: Cache, fileId: string, widgetMetrics: any) => {
  if (!fileId || !widgetMetrics) {
    return;
  }

  inspectCache(cache, 'Query', 'file', (field) => {
    const args = field.arguments as any;
    if (args.filters?.id == fileId) {
      updateCache<FileQuery, FileQueryVariables>(cache, FileDocument, args, (data) => {
        if (data?.file) {
          data.file.widgetMetrics = widgetMetrics;
        }
        return data;
      });
    }
  });
};

export const createActionItemAndUpdateMetrics = (
  result: CreateActionItemAndUpdateMetricsMutation,
  args: any,
  cache: Cache,
  info: any,
) => {
  const widgetFile = result?.createActionItem?.widgetFile;

  if (widgetFile && widgetFile.id) {
    handleFileWidgetMetricsUpdate(cache, widgetFile.id, widgetFile);
  }

  invalidateQuery(['actionItems', 'paginatedGroupedActionItems', 'files', 'peerFeedback'])(
    result,
    args,
    cache,
    info,
  );
};

export const updateActionItemAndUpdateMetrics = (
  result: UpdateActionItemAndUpdateMetricsMutation,
  args: any,
  cache: Cache,
  info: any,
) => {
  const widgetFile = result?.updateActionItem?.widgetFile;

  if (widgetFile && widgetFile.id) {
    handleFileWidgetMetricsUpdate(cache, widgetFile.id, widgetFile.widgetMetrics);
  }

  updateActionItemMutation()(result as any, args, cache, info);
};

export const deleteActionItemAndUpdateMetrics = (
  result: DeleteActionItemAndUpdateMetricsMutation,
  args: any,
  cache: Cache,
  info: any,
) => {
  const widgetFile = result?.deleteActionItem?.widgetFile;

  if (widgetFile && widgetFile.id) {
    handleFileWidgetMetricsUpdate(cache, widgetFile.id, widgetFile);
  }

  deleteActionItemMutation()(result as any, args, cache, info);
};

export function deleteActionItemMutation(): UpdateResolver {
  return (result, args, cache, info) => {
    invalidateQuery(['actionItems', 'paginatedGroupedActionItems', 'files', 'peerFeedback'])(
      result,
      args,
      cache,
      info,
    );
    const updatedEntities = (result?.deleteActionItem as any)?.actionItems as any[];
    if (!updatedEntities?.length) {
      return;
    }
    const updatesMap = new Map<string, number>();
    updatedEntities.forEach((item: any) => {
      if (!isNil(item.totalTimeSpent)) {
        updatesMap.set(item.entityId, item.totalTimeSpent);
      }
    });

    //1. updating cache for "paginatedGroupedActionItems"
    inspectCache(cache, 'Query', 'paginatedGroupedActionItems', (field) => {
      const args = field.arguments as PaginatedGroupedActionItemsQueryVariables;
      if (!args.first) {
        return;
      }
      updateCache<PaginatedGroupedActionItemsQuery, PaginatedGroupedActionItemsQueryVariables>(
        cache,
        PaginatedGroupedActionItemsDocument,
        args,
        (queryData) => {
          if (queryData?.paginatedGroupedActionItems?.edges) {
            queryData.paginatedGroupedActionItems.edges.forEach((groupEdge) => {
              if (groupEdge.node.groupedEntity.edges) {
                groupEdge.node.groupedEntity.edges.forEach((itemEdge) => {
                  const newTotalTimeSpent = updatesMap.get(itemEdge.node.id);
                  if (typeof newTotalTimeSpent !== 'undefined') {
                    itemEdge.node.totalTimeSpent = newTotalTimeSpent;
                  }
                });
              }
            });
          }
          return queryData;
        },
      );
    });

    //2. updating cache for "actionItems" (subtasks)
    inspectCache(cache, 'Query', 'actionItems', (field) => {
      const args = field.arguments as ActionItemsQueryVariables;
      updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
        cache,
        ActionItemsDocument,
        args,
        (queryData) => {
          if (queryData?.actionItems?.edges) {
            queryData.actionItems.edges.forEach((itemEdge) => {
              const newTotalTimeSpent = updatesMap.get(itemEdge.node.id);
              if (typeof newTotalTimeSpent !== 'undefined') {
                itemEdge.node.totalTimeSpent = newTotalTimeSpent;
              }
            });
          }
          return queryData;
        },
      );
    });

    //3. updating cache for "actionItem"
    inspectCache(cache, 'Query', 'actionItem', (field) => {
      const args = field.arguments as TaskDetailsQueryVariables;
      updateCache<TaskDetailsQuery, TaskDetailsQueryVariables>(
        cache,
        TaskDetailsDocument,
        args,
        (queryData) => {
          if (queryData?.actionItem?.actionItem) {
            const newTotalTimeSpent = updatesMap.get(queryData.actionItem.actionItem.id);
            if (typeof newTotalTimeSpent !== 'undefined') {
              queryData.actionItem.actionItem.totalTimeSpent = newTotalTimeSpent;
            }
          }
          return queryData;
        },
      );
    });
  };
}

export function updateTimeLogMutation(): UpdateResolver {
  return (result, args, cache, info) => {
    invalidateQuery('timeLogs')(result, args, cache, info);
    const updatedEntities = (result?.updateTimeLog as any)?.updatedEntitiesTimeSpent;

    if (!updatedEntities?.length) {
      return;
    }

    const updatesMap = new Map<string, number>();
    updatedEntities.forEach((item: any) => {
      if (!isNil(item.totalTimeSpent)) {
        updatesMap.set(item.entityId, item.totalTimeSpent);
      }
    });

    //1. updating cache for "paginatedGroupedActionItems"
    inspectCache(cache, 'Query', 'paginatedGroupedActionItems', (field) => {
      const args = field.arguments as PaginatedGroupedActionItemsQueryVariables;
      if (!args.first) {
        return;
      }
      updateCache<PaginatedGroupedActionItemsQuery, PaginatedGroupedActionItemsQueryVariables>(
        cache,
        PaginatedGroupedActionItemsDocument,
        args,
        (queryData) => {
          if (queryData?.paginatedGroupedActionItems?.edges) {
            queryData.paginatedGroupedActionItems.edges.forEach((groupEdge) => {
              if (groupEdge.node.groupedEntity.edges) {
                groupEdge.node.groupedEntity.edges.forEach((itemEdge) => {
                  const newTotalTimeSpent = updatesMap.get(itemEdge.node.id);
                  if (typeof newTotalTimeSpent !== 'undefined') {
                    itemEdge.node.totalTimeSpent = newTotalTimeSpent;
                  }
                });
              }
            });
          }
          return queryData;
        },
      );
    });

    //2. updating cache for "actionItems" (subtasks)
    inspectCache(cache, 'Query', 'actionItems', (field) => {
      const args = field.arguments as ActionItemsQueryVariables;
      updateCache<ActionItemsQuery, ActionItemsQueryVariables>(
        cache,
        ActionItemsDocument,
        args,
        (queryData) => {
          if (queryData?.actionItems?.edges) {
            queryData.actionItems.edges.forEach((itemEdge) => {
              const newTotalTimeSpent = updatesMap.get(itemEdge.node.id);
              if (typeof newTotalTimeSpent !== 'undefined') {
                itemEdge.node.totalTimeSpent = newTotalTimeSpent;
              }
            });
          }
          return queryData;
        },
      );
    });

    //3. updating cache for "actionItem"
    inspectCache(cache, 'Query', 'actionItem', (field) => {
      const args = field.arguments as TaskDetailsQueryVariables;
      updateCache<TaskDetailsQuery, TaskDetailsQueryVariables>(
        cache,
        TaskDetailsDocument,
        args,
        (queryData) => {
          if (queryData?.actionItem?.actionItem) {
            const newTotalTimeSpent = updatesMap.get(queryData.actionItem.actionItem.id);
            if (typeof newTotalTimeSpent !== 'undefined') {
              queryData.actionItem.actionItem.totalTimeSpent = newTotalTimeSpent;
            }
          }
          return queryData;
        },
      );
    });
  };
}

export function updateEntityAttachmentsMutation(): UpdateResolver {
  return (result, args, cache, info) => {
    const allQueries = cache.inspectFields('Query');
    const { data } = (args ?? {}) as any;
    const isOrderChanged = data?.order;
    const actionItem = (result as UpdateEntityAttachmentMutation)?.updateEntityAttachments as any;
    handleTaskDetailUpdageChange(cache, data, actionItem as ActionItemFragment);
    invalidateQuery(['getAttachments'])(result, args, cache, info);
  };
}

export function updateEntityTagsMutation(): UpdateResolver {
  return (result, args, cache) => {
    const allQueries = cache.inspectFields('Query');
    const { input } = (args ?? {}) as any;
    const entityId = input?.entityId;
    const tags = (result as UpdateEntityTagsMutation)?.updateEntityTags.entityTags;
    if (tags) {
      handleTaskDetailUpdageChange(cache, input, { tags, id: entityId } as ActionItemFragment);
    }
  };
}
